import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";
import FormExtras from "../../../../common/components/formExtras";
import { FormRecordProvider } from "../../../../common/controls/forms/FormRecordContext";
import { TenantProfileUpdateStore } from "../../store";
import { PersonType } from "../../../../api/graphql";
import { InputReference } from "../../../common/controls";
import { TenantProfileLegalNameCustomField } from "../custom/TenantProfileLegalNameCustomField";
import { TenantProfileDndaCompletenessNotice } from "../custom/TenantProfileDndaCompletenessNotice";
import { CreateEntityDialog } from "../../../common/controls";
import { PublisherCreate } from "../../../publisher/components/create/PublisherCreate";
import { PublisherCreateStore } from "../../../publisher/store";
import { InputStringMask } from "../../../common/controls";
import { InputString } from "../../../common/controls";
import { FormControl } from "@mui/material";
import { CountryCreate } from "../../../country/components/create/CountryCreate";
import { CountryCreateStore } from "../../../country/store";
import { ProvinceStateCreate } from "../../../provinceState/components/create/ProvinceStateCreate";
import { ProvinceStateCreateStore } from "../../../provinceState/store";
import { VatStatusCreate } from "../../../vatStatus/components/create/VatStatusCreate";
import { VatStatusCreateStore } from "../../../vatStatus/store";
import { GrossReceiptsStatusCreate } from "../../../grossReceiptsStatus/components/create/GrossReceiptsStatusCreate";
import { GrossReceiptsStatusCreateStore } from "../../../grossReceiptsStatus/store";
import { CurrencyCreate } from "../../../currency/components/create/CurrencyCreate";
import { CurrencyCreateStore } from "../../../currency/store";
import { InputSecret } from "../../../common/controls";

export type TenantProfileUpdateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  recordId?: string;
  personTypeOptions: { id: string; displayId: string }[];
  personTypeError?: string;
  personType: PersonType | "";
  onPersonTypeChange?: (value: PersonType | "") => void;
  legalName: string;
  legalNameSelectedValue?: { displayId: string; id: string } | null;
  onLegalNameChange?: (value: string) => void;
  legalNameError?: string;
  ownPublisherSelectedValue?: { displayId: string; id: string } | null;
  onOwnPublisherChange?: (value: string) => void;
  onOwnPublisherSelected?: (
    value: { displayId: string; id: string } | null
  ) => void;
  ownPublisherOptions?: { displayId: string; id: string }[];
  ownPublisherError?: string;
  businessId: string;
  businessIdSelectedValue?: { displayId: string; id: string } | null;
  onBusinessIdChange?: (value: string) => void;
  businessIdError?: string;
  legalEmail: string;
  legalEmailSelectedValue?: { displayId: string; id: string } | null;
  onLegalEmailChange?: (value: string) => void;
  legalEmailError?: string;
  streetAndNumber: string;
  streetAndNumberSelectedValue?: { displayId: string; id: string } | null;
  onStreetAndNumberChange?: (value: string) => void;
  streetAndNumberError?: string;
  floor: string;
  floorSelectedValue?: { displayId: string; id: string } | null;
  onFloorChange?: (value: string) => void;
  floorError?: string;
  suite: string;
  suiteSelectedValue?: { displayId: string; id: string } | null;
  onSuiteChange?: (value: string) => void;
  suiteError?: string;
  postalCode: string;
  postalCodeSelectedValue?: { displayId: string; id: string } | null;
  onPostalCodeChange?: (value: string) => void;
  postalCodeError?: string;
  countrySelectedValue?: { displayId: string; id: string } | null;
  onCountryChange?: (value: string) => void;
  onCountrySelected?: (value: { displayId: string; id: string } | null) => void;
  countryOptions?: { displayId: string; id: string }[];
  countryError?: string;
  provinceSelectedValue?: { displayId: string; id: string } | null;
  onProvinceChange?: (value: string) => void;
  onProvinceSelected?: (
    value: { displayId: string; id: string } | null
  ) => void;
  provinceOptions?: { displayId: string; id: string }[];
  provinceError?: string;
  city: string;
  citySelectedValue?: { displayId: string; id: string } | null;
  onCityChange?: (value: string) => void;
  cityError?: string;
  vatStatusSelectedValue?: { displayId: string; id: string } | null;
  onVatStatusChange?: (value: string) => void;
  onVatStatusSelected?: (
    value: { displayId: string; id: string } | null
  ) => void;
  vatStatusOptions?: { displayId: string; id: string }[];
  vatStatusError?: string;
  taxId: string;
  taxIdSelectedValue?: { displayId: string; id: string } | null;
  onTaxIdChange?: (value: string) => void;
  taxIdError?: string;
  grossReceiptsStatusSelectedValue?: { displayId: string; id: string } | null;
  onGrossReceiptsStatusChange?: (value: string) => void;
  onGrossReceiptsStatusSelected?: (
    value: { displayId: string; id: string } | null
  ) => void;
  grossReceiptsStatusOptions?: { displayId: string; id: string }[];
  grossReceiptsStatusError?: string;
  grossReceiptsId: string;
  grossReceiptsIdSelectedValue?: { displayId: string; id: string } | null;
  onGrossReceiptsIdChange?: (value: string) => void;
  grossReceiptsIdError?: string;
  billingEmail: string;
  billingEmailSelectedValue?: { displayId: string; id: string } | null;
  onBillingEmailChange?: (value: string) => void;
  billingEmailError?: string;
  billingCurrencySelectedValue?: { displayId: string; id: string } | null;
  onBillingCurrencyChange?: (value: string) => void;
  onBillingCurrencySelected?: (
    value: { displayId: string; id: string } | null
  ) => void;
  billingCurrencyOptions?: { displayId: string; id: string }[];
  billingCurrencyError?: string;
  printerName: string;
  printerNameSelectedValue?: { displayId: string; id: string } | null;
  onPrinterNameChange?: (value: string) => void;
  printerNameError?: string;
  printerStreetAndNumber: string;
  printerStreetAndNumberSelectedValue?: {
    displayId: string;
    id: string;
  } | null;
  onPrinterStreetAndNumberChange?: (value: string) => void;
  printerStreetAndNumberError?: string;
  printerFloor: string;
  printerFloorSelectedValue?: { displayId: string; id: string } | null;
  onPrinterFloorChange?: (value: string) => void;
  printerFloorError?: string;
  printerSuite: string;
  printerSuiteSelectedValue?: { displayId: string; id: string } | null;
  onPrinterSuiteChange?: (value: string) => void;
  printerSuiteError?: string;
  printerPostalCode: string;
  printerPostalCodeSelectedValue?: { displayId: string; id: string } | null;
  onPrinterPostalCodeChange?: (value: string) => void;
  printerPostalCodeError?: string;
  printerCountrySelectedValue?: { displayId: string; id: string } | null;
  onPrinterCountryChange?: (value: string) => void;
  onPrinterCountrySelected?: (
    value: { displayId: string; id: string } | null
  ) => void;
  printerCountryOptions?: { displayId: string; id: string }[];
  printerCountryError?: string;
  printerProvinceSelectedValue?: { displayId: string; id: string } | null;
  onPrinterProvinceChange?: (value: string) => void;
  onPrinterProvinceSelected?: (
    value: { displayId: string; id: string } | null
  ) => void;
  printerProvinceOptions?: { displayId: string; id: string }[];
  printerProvinceError?: string;
  printerCity: string;
  printerCitySelectedValue?: { displayId: string; id: string } | null;
  onPrinterCityChange?: (value: string) => void;
  printerCityError?: string;
  sadaicFtpUrl: string;
  sadaicFtpUrlSelectedValue?: { displayId: string; id: string } | null;
  onSadaicFtpUrlChange?: (value: string) => void;
  sadaicFtpUrlError?: string;
  sadaicFtpUser: string;
  sadaicFtpUserSelectedValue?: { displayId: string; id: string } | null;
  onSadaicFtpUserChange?: (value: string) => void;
  sadaicFtpUserError?: string;
  sadaicFtpPassword: string;
  sadaicFtpPasswordSelectedValue?: { displayId: string; id: string } | null;
  onSadaicFtpPasswordChange?: (value: string) => void;
  sadaicFtpPasswordError?: string;
  sadaicEmail: string;
  sadaicEmailSelectedValue?: { displayId: string; id: string } | null;
  onSadaicEmailChange?: (value: string) => void;
  sadaicEmailError?: string;
  tenantProfileUpdateStore: TenantProfileUpdateStore;
};

export const TenantProfileUpdateForm: React.FC<TenantProfileUpdateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      recordId,
      personTypeOptions = [],
      personTypeError,
      personType = "" as PersonType | "",
      onPersonTypeChange = () => {},
      legalName = "",
      legalNameError,
      onLegalNameChange = () => {},
      onOwnPublisherSelected = () => {},
      ownPublisherOptions = [],
      ownPublisherSelectedValue = null,
      ownPublisherError,
      onOwnPublisherChange = () => {},
      businessId = "",
      businessIdError,
      onBusinessIdChange = () => {},
      legalEmail = "",
      legalEmailError,
      onLegalEmailChange = () => {},
      streetAndNumber = "",
      streetAndNumberError,
      onStreetAndNumberChange = () => {},
      floor = "",
      floorError,
      onFloorChange = () => {},
      suite = "",
      suiteError,
      onSuiteChange = () => {},
      postalCode = "",
      postalCodeError,
      onPostalCodeChange = () => {},
      onCountrySelected = () => {},
      countryOptions = [],
      countrySelectedValue = null,
      countryError,
      onCountryChange = () => {},
      onProvinceSelected = () => {},
      provinceOptions = [],
      provinceSelectedValue = null,
      provinceError,
      onProvinceChange = () => {},
      city = "",
      cityError,
      onCityChange = () => {},
      onVatStatusSelected = () => {},
      vatStatusOptions = [],
      vatStatusSelectedValue = null,
      vatStatusError,
      onVatStatusChange = () => {},
      taxId = "",
      taxIdError,
      onTaxIdChange = () => {},
      onGrossReceiptsStatusSelected = () => {},
      grossReceiptsStatusOptions = [],
      grossReceiptsStatusSelectedValue = null,
      grossReceiptsStatusError,
      onGrossReceiptsStatusChange = () => {},
      grossReceiptsId = "",
      grossReceiptsIdError,
      onGrossReceiptsIdChange = () => {},
      billingEmail = "",
      billingEmailError,
      onBillingEmailChange = () => {},
      onBillingCurrencySelected = () => {},
      billingCurrencyOptions = [],
      billingCurrencySelectedValue = null,
      billingCurrencyError,
      onBillingCurrencyChange = () => {},
      printerName = "",
      printerNameError,
      onPrinterNameChange = () => {},
      printerStreetAndNumber = "",
      printerStreetAndNumberError,
      onPrinterStreetAndNumberChange = () => {},
      printerFloor = "",
      printerFloorError,
      onPrinterFloorChange = () => {},
      printerSuite = "",
      printerSuiteError,
      onPrinterSuiteChange = () => {},
      printerPostalCode = "",
      printerPostalCodeError,
      onPrinterPostalCodeChange = () => {},
      onPrinterCountrySelected = () => {},
      printerCountryOptions = [],
      printerCountrySelectedValue = null,
      printerCountryError,
      onPrinterCountryChange = () => {},
      onPrinterProvinceSelected = () => {},
      printerProvinceOptions = [],
      printerProvinceSelectedValue = null,
      printerProvinceError,
      onPrinterProvinceChange = () => {},
      printerCity = "",
      printerCityError,
      onPrinterCityChange = () => {},
      sadaicFtpUrl = "",
      sadaicFtpUrlError,
      onSadaicFtpUrlChange = () => {},
      sadaicFtpUser = "",
      sadaicFtpUserError,
      onSadaicFtpUserChange = () => {},
      sadaicFtpPassword = "",
      sadaicFtpPasswordError,
      onSadaicFtpPasswordChange = () => {},
      sadaicEmail = "",
      sadaicEmailError,
      onSadaicEmailChange = () => {},
      tenantProfileUpdateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelUpdatedEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormRecordProvider
              recordId={recordId}
              values={{
                personType,
                legalName,
                ownPublisher: ownPublisherSelectedValue,
                businessId,
                legalEmail,
                streetAndNumber,
                floor,
                suite,
                postalCode,
                country: countrySelectedValue,
                province: provinceSelectedValue,
                city,
                vatStatus: vatStatusSelectedValue,
                taxId,
                grossReceiptsStatus: grossReceiptsStatusSelectedValue,
                grossReceiptsId,
                billingEmail,
                billingCurrency: billingCurrencySelectedValue,
                printerName,
                printerStreetAndNumber,
                printerFloor,
                printerSuite,
                printerPostalCode,
                printerCountry: printerCountrySelectedValue,
                printerProvince: printerProvinceSelectedValue,
                printerCity,
                sadaicFtpUrl,
                sadaicFtpUser,
                sadaicFtpPassword,
                sadaicEmail,
              }}
            >
              <TenantProfileDndaCompletenessNotice
                personType={personType}
                legalName={legalName}
                businessId={businessId}
              />
              <FormExtras
                entity="TenantProfile"
                mode="update"
                recordId={recordId}
                values={{
                  personType,
                  legalName,
                  ownPublisher: ownPublisherSelectedValue,
                  businessId,
                  legalEmail,
                  streetAndNumber,
                  floor,
                  suite,
                  postalCode,
                  country: countrySelectedValue,
                  province: provinceSelectedValue,
                  city,
                  vatStatus: vatStatusSelectedValue,
                  taxId,
                  grossReceiptsStatus: grossReceiptsStatusSelectedValue,
                  grossReceiptsId,
                  billingEmail,
                  billingCurrency: billingCurrencySelectedValue,
                  printerName,
                  printerStreetAndNumber,
                  printerFloor,
                  printerSuite,
                  printerPostalCode,
                  printerCountry: printerCountrySelectedValue,
                  printerProvince: printerProvinceSelectedValue,
                  printerCity,
                  sadaicFtpUrl,
                  sadaicFtpUser,
                  sadaicFtpPassword,
                  sadaicEmail,
                }}
                options={{
                  ownPublisher: ownPublisherOptions,
                  country: countryOptions,
                  province: provinceOptions,
                  vatStatus: vatStatusOptions,
                  grossReceiptsStatus: grossReceiptsStatusOptions,
                  billingCurrency: billingCurrencyOptions,
                  printerCountry: printerCountryOptions,
                  printerProvince: printerProvinceOptions,
                }}
                onPatch={(campo: string, valor: any) => {
                  switch (campo) {
                    case "personType":
                      onPersonTypeChange?.(valor);
                      break;
                    case "legalName":
                      onLegalNameChange?.(valor);
                      break;
                    case "ownPublisher":
                      onOwnPublisherSelected?.(valor);
                      break;
                    case "businessId":
                      onBusinessIdChange?.(valor);
                      break;
                    case "legalEmail":
                      onLegalEmailChange?.(valor);
                      break;
                    case "streetAndNumber":
                      onStreetAndNumberChange?.(valor);
                      break;
                    case "floor":
                      onFloorChange?.(valor);
                      break;
                    case "suite":
                      onSuiteChange?.(valor);
                      break;
                    case "postalCode":
                      onPostalCodeChange?.(valor);
                      break;
                    case "country":
                      onCountrySelected?.(valor);
                      break;
                    case "province":
                      onProvinceSelected?.(valor);
                      break;
                    case "city":
                      onCityChange?.(valor);
                      break;
                    case "vatStatus":
                      onVatStatusSelected?.(valor);
                      break;
                    case "taxId":
                      onTaxIdChange?.(valor);
                      break;
                    case "grossReceiptsStatus":
                      onGrossReceiptsStatusSelected?.(valor);
                      break;
                    case "grossReceiptsId":
                      onGrossReceiptsIdChange?.(valor);
                      break;
                    case "billingEmail":
                      onBillingEmailChange?.(valor);
                      break;
                    case "billingCurrency":
                      onBillingCurrencySelected?.(valor);
                      break;
                    case "printerName":
                      onPrinterNameChange?.(valor);
                      break;
                    case "printerStreetAndNumber":
                      onPrinterStreetAndNumberChange?.(valor);
                      break;
                    case "printerFloor":
                      onPrinterFloorChange?.(valor);
                      break;
                    case "printerSuite":
                      onPrinterSuiteChange?.(valor);
                      break;
                    case "printerPostalCode":
                      onPrinterPostalCodeChange?.(valor);
                      break;
                    case "printerCountry":
                      onPrinterCountrySelected?.(valor);
                      break;
                    case "printerProvince":
                      onPrinterProvinceSelected?.(valor);
                      break;
                    case "printerCity":
                      onPrinterCityChange?.(valor);
                      break;
                    case "sadaicFtpUrl":
                      onSadaicFtpUrlChange?.(valor);
                      break;
                    case "sadaicFtpUser":
                      onSadaicFtpUserChange?.(valor);
                      break;
                    case "sadaicFtpPassword":
                      onSadaicFtpPasswordChange?.(valor);
                      break;
                    case "sadaicEmail":
                      onSadaicEmailChange?.(valor);
                      break;
                  }
                }}
              />

              <GroupContainer
                id="business"
                errorText={tenantProfileUpdateStore.getCardErrors("business")}
                title={langStore.groupTenantProfileBusinessLabel}
                collapsible={true}
                expanded={tenantProfileUpdateStore.getCardState("business")}
                toggleExpand={() =>
                  tenantProfileUpdateStore.toggleCardState("business")
                }
              >
                <FormControl fullWidth>
                  <InputReference
                    id="personType"
                    autoFocus
                    disabled={false}
                    error={Boolean(personTypeError)}
                    helperText={
                      personTypeError ||
                      tenantProfileUpdateStore.warnings["personType"]?.message
                    }
                    label={langStore.TenantProfilePersonTypeLabel}
                    options={personTypeOptions}
                    nullText={langStore.optionalFieldHint}
                    selectedValue={
                      personType
                        ? {
                            id: personType,
                            displayId:
                              personTypeOptions.find(
                                (o: { id: string }) => o.id === personType
                              )?.displayId || personType,
                          }
                        : null
                    }
                    onChange={() => {}}
                    onSelect={(
                      value: { id: string; displayId: string } | null
                    ) =>
                      onPersonTypeChange(
                        (value?.id as PersonType | "") ||
                          ("" as PersonType | "")
                      )
                    }
                  />
                  <TenantProfileLegalNameCustomField
                    value={legalName}
                    error={Boolean(legalNameError)}
                    helperText={
                      legalNameError ||
                      tenantProfileUpdateStore.warnings["legalName"]?.message
                    }
                    {...{ optional: true }}
                    label={langStore.TenantProfileLegalNameLabel}
                    description={langStore.TenantProfileLegalNameDescription}
                    onChange={(value: string) => onLegalNameChange(value)}
                  />
                  <InputReference
                    id="ownPublisher"
                    label={langStore.TenantProfileOwnPublisherLabel}
                    error={Boolean(ownPublisherError)}
                    helperText={
                      ownPublisherError ||
                      tenantProfileUpdateStore.warnings["ownPublisher"]?.message
                    }
                    selectedValue={ownPublisherSelectedValue}
                    onChange={(value: string) => onOwnPublisherChange(value)}
                    onSelect={(
                      value: { displayId: string; id: string } | null
                    ) => onOwnPublisherSelected(value)}
                    options={ownPublisherOptions}
                    description={langStore.TenantProfileOwnPublisherDescription}
                    nullText={langStore.optionalFieldHint}
                    canCreate={rootStore.authStore.hasPermissions(
                      "publisher_create"
                    )}
                    renderCreateDialog={(dialogProps) => (
                      <CreateEntityDialog
                        {...dialogProps}
                        createStore={(rootStore) =>
                          new PublisherCreateStore(rootStore)
                        }
                        renderForm={(store) => (
                          <PublisherCreate
                            publisherCreateStore={store}
                            showToolbox={false}
                            showSaveFooter={false}
                          />
                        )}
                        saveMethod="saveNewPublisher"
                        cancelMethod="cancelNewPublisher"
                      />
                    )}
                  />
                  <InputStringMask
                    value={businessId}
                    error={Boolean(businessIdError)}
                    warning={
                      !businessIdError &&
                      Boolean(tenantProfileUpdateStore.warnings["businessId"])
                    }
                    helperText={
                      businessIdError ||
                      tenantProfileUpdateStore.warnings["businessId"]?.message
                    }
                    label={langStore.TenantProfileBusinessIdLabel}
                    onChange={(value: string) => onBusinessIdChange(value)}
                    maskConfig={{
                      mask: "99-9999999-9",
                      masks: ["99-9999999-9", "99-99999999-9"],
                    }}
                    nullText={langStore.optionalFieldHint}
                    description={langStore.TenantProfileBusinessIdDescription}
                  />
                  <InputString
                    value={legalEmail}
                    error={Boolean(legalEmailError)}
                    warning={
                      !legalEmailError &&
                      Boolean(tenantProfileUpdateStore.warnings["legalEmail"])
                    }
                    helperText={
                      legalEmailError ||
                      tenantProfileUpdateStore.warnings["legalEmail"]?.message
                    }
                    label={langStore.TenantProfileLegalEmailLabel}
                    onChange={(value: string) => onLegalEmailChange(value)}
                    nullText={langStore.optionalFieldHint}
                    description={langStore.TenantProfileLegalEmailDescription}
                  />
                </FormControl>
              </GroupContainer>

              <GroupContainer
                id="address"
                errorText={tenantProfileUpdateStore.getCardErrors("address")}
                title={langStore.groupTenantProfileAddressLabel}
                collapsible={true}
                expanded={tenantProfileUpdateStore.getCardState("address")}
                toggleExpand={() =>
                  tenantProfileUpdateStore.toggleCardState("address")
                }
              >
                <FormControl fullWidth>
                  <InputString
                    value={streetAndNumber}
                    error={Boolean(streetAndNumberError)}
                    warning={
                      !streetAndNumberError &&
                      Boolean(
                        tenantProfileUpdateStore.warnings["streetAndNumber"]
                      )
                    }
                    helperText={
                      streetAndNumberError ||
                      tenantProfileUpdateStore.warnings["streetAndNumber"]
                        ?.message
                    }
                    label={langStore.TenantProfileStreetAndNumberLabel}
                    onChange={(value: string) => onStreetAndNumberChange(value)}
                    nullText={langStore.optionalFieldHint}
                    description={
                      langStore.TenantProfileStreetAndNumberDescription
                    }
                  />
                  <InputString
                    value={floor}
                    error={Boolean(floorError)}
                    warning={
                      !floorError &&
                      Boolean(tenantProfileUpdateStore.warnings["floor"])
                    }
                    helperText={
                      floorError ||
                      tenantProfileUpdateStore.warnings["floor"]?.message
                    }
                    label={langStore.TenantProfileFloorLabel}
                    onChange={(value: string) => onFloorChange(value)}
                    nullText={langStore.optionalFieldHint}
                    description={langStore.TenantProfileFloorDescription}
                  />
                  <InputString
                    value={suite}
                    error={Boolean(suiteError)}
                    warning={
                      !suiteError &&
                      Boolean(tenantProfileUpdateStore.warnings["suite"])
                    }
                    helperText={
                      suiteError ||
                      tenantProfileUpdateStore.warnings["suite"]?.message
                    }
                    label={langStore.TenantProfileSuiteLabel}
                    onChange={(value: string) => onSuiteChange(value)}
                    nullText={langStore.optionalFieldHint}
                    description={langStore.TenantProfileSuiteDescription}
                  />
                  <InputString
                    value={postalCode}
                    error={Boolean(postalCodeError)}
                    warning={
                      !postalCodeError &&
                      Boolean(tenantProfileUpdateStore.warnings["postalCode"])
                    }
                    helperText={
                      postalCodeError ||
                      tenantProfileUpdateStore.warnings["postalCode"]?.message
                    }
                    label={langStore.TenantProfilePostalCodeLabel}
                    onChange={(value: string) => onPostalCodeChange(value)}
                    nullText={langStore.optionalFieldHint}
                    description={langStore.TenantProfilePostalCodeDescription}
                  />
                  <InputReference
                    id="country"
                    label={langStore.TenantProfileCountryLabel}
                    error={Boolean(countryError)}
                    helperText={
                      countryError ||
                      tenantProfileUpdateStore.warnings["country"]?.message
                    }
                    selectedValue={countrySelectedValue}
                    onChange={(value: string) => onCountryChange(value)}
                    onSelect={(
                      value: { displayId: string; id: string } | null
                    ) => onCountrySelected(value)}
                    options={countryOptions}
                    description={langStore.TenantProfileCountryDescription}
                    nullText={langStore.optionalFieldHint}
                    canCreate={rootStore.authStore.hasPermissions(
                      "country_create"
                    )}
                    renderCreateDialog={(dialogProps) => (
                      <CreateEntityDialog
                        {...dialogProps}
                        createStore={(rootStore) =>
                          new CountryCreateStore(rootStore)
                        }
                        renderForm={(store) => (
                          <CountryCreate
                            countryCreateStore={store}
                            showToolbox={false}
                            showSaveFooter={false}
                          />
                        )}
                        saveMethod="saveNewCountry"
                        cancelMethod="cancelNewCountry"
                      />
                    )}
                  />
                  <InputReference
                    id="province"
                    label={langStore.TenantProfileProvinceLabel}
                    error={Boolean(provinceError)}
                    helperText={
                      provinceError ||
                      tenantProfileUpdateStore.warnings["province"]?.message
                    }
                    selectedValue={provinceSelectedValue}
                    onChange={(value: string) => onProvinceChange(value)}
                    onSelect={(
                      value: { displayId: string; id: string } | null
                    ) => onProvinceSelected(value)}
                    options={provinceOptions}
                    description={langStore.TenantProfileProvinceDescription}
                    nullText={langStore.optionalFieldHint}
                    canCreate={rootStore.authStore.hasPermissions(
                      "provinceState_create"
                    )}
                    renderCreateDialog={(dialogProps) => (
                      <CreateEntityDialog
                        {...dialogProps}
                        createStore={(rootStore) =>
                          new ProvinceStateCreateStore(rootStore)
                        }
                        renderForm={(store) => (
                          <ProvinceStateCreate
                            provinceStateCreateStore={store}
                            showToolbox={false}
                            showSaveFooter={false}
                          />
                        )}
                        saveMethod="saveNewProvinceState"
                        cancelMethod="cancelNewProvinceState"
                      />
                    )}
                  />
                  <InputString
                    value={city}
                    error={Boolean(cityError)}
                    warning={
                      !cityError &&
                      Boolean(tenantProfileUpdateStore.warnings["city"])
                    }
                    helperText={
                      cityError ||
                      tenantProfileUpdateStore.warnings["city"]?.message
                    }
                    label={langStore.TenantProfileCityLabel}
                    onChange={(value: string) => onCityChange(value)}
                    nullText={langStore.optionalFieldHint}
                    description={langStore.TenantProfileCityDescription}
                  />
                </FormControl>
              </GroupContainer>

              <GroupContainer
                id="billing"
                errorText={tenantProfileUpdateStore.getCardErrors("billing")}
                title={langStore.groupTenantProfileBillingLabel}
                collapsible={true}
                expanded={tenantProfileUpdateStore.getCardState("billing")}
                toggleExpand={() =>
                  tenantProfileUpdateStore.toggleCardState("billing")
                }
              >
                <FormControl fullWidth>
                  <InputReference
                    id="vatStatus"
                    label={langStore.TenantProfileVatStatusLabel}
                    error={Boolean(vatStatusError)}
                    helperText={
                      vatStatusError ||
                      tenantProfileUpdateStore.warnings["vatStatus"]?.message
                    }
                    selectedValue={vatStatusSelectedValue}
                    onChange={(value: string) => onVatStatusChange(value)}
                    onSelect={(
                      value: { displayId: string; id: string } | null
                    ) => onVatStatusSelected(value)}
                    options={vatStatusOptions}
                    description={langStore.TenantProfileVatStatusDescription}
                    nullText={langStore.optionalFieldHint}
                    canCreate={rootStore.authStore.hasPermissions(
                      "vatStatus_create"
                    )}
                    renderCreateDialog={(dialogProps) => (
                      <CreateEntityDialog
                        {...dialogProps}
                        createStore={(rootStore) =>
                          new VatStatusCreateStore(rootStore)
                        }
                        renderForm={(store) => (
                          <VatStatusCreate
                            vatStatusCreateStore={store}
                            showToolbox={false}
                            showSaveFooter={false}
                          />
                        )}
                        saveMethod="saveNewVatStatus"
                        cancelMethod="cancelNewVatStatus"
                      />
                    )}
                  />
                  <InputString
                    value={taxId}
                    error={Boolean(taxIdError)}
                    warning={
                      !taxIdError &&
                      Boolean(tenantProfileUpdateStore.warnings["taxId"])
                    }
                    helperText={
                      taxIdError ||
                      tenantProfileUpdateStore.warnings["taxId"]?.message
                    }
                    label={langStore.TenantProfileTaxIdLabel}
                    onChange={(value: string) => onTaxIdChange(value)}
                    nullText={langStore.optionalFieldHint}
                    description={langStore.TenantProfileTaxIdDescription}
                  />
                  <InputReference
                    id="grossReceiptsStatus"
                    label={langStore.TenantProfileGrossReceiptsStatusLabel}
                    error={Boolean(grossReceiptsStatusError)}
                    helperText={
                      grossReceiptsStatusError ||
                      tenantProfileUpdateStore.warnings["grossReceiptsStatus"]
                        ?.message
                    }
                    selectedValue={grossReceiptsStatusSelectedValue}
                    onChange={(value: string) =>
                      onGrossReceiptsStatusChange(value)
                    }
                    onSelect={(
                      value: { displayId: string; id: string } | null
                    ) => onGrossReceiptsStatusSelected(value)}
                    options={grossReceiptsStatusOptions}
                    description={
                      langStore.TenantProfileGrossReceiptsStatusDescription
                    }
                    nullText={langStore.optionalFieldHint}
                    canCreate={rootStore.authStore.hasPermissions(
                      "grossReceiptsStatus_create"
                    )}
                    renderCreateDialog={(dialogProps) => (
                      <CreateEntityDialog
                        {...dialogProps}
                        createStore={(rootStore) =>
                          new GrossReceiptsStatusCreateStore(rootStore)
                        }
                        renderForm={(store) => (
                          <GrossReceiptsStatusCreate
                            grossReceiptsStatusCreateStore={store}
                            showToolbox={false}
                            showSaveFooter={false}
                          />
                        )}
                        saveMethod="saveNewGrossReceiptsStatus"
                        cancelMethod="cancelNewGrossReceiptsStatus"
                      />
                    )}
                  />
                  <InputString
                    value={grossReceiptsId}
                    error={Boolean(grossReceiptsIdError)}
                    warning={
                      !grossReceiptsIdError &&
                      Boolean(
                        tenantProfileUpdateStore.warnings["grossReceiptsId"]
                      )
                    }
                    helperText={
                      grossReceiptsIdError ||
                      tenantProfileUpdateStore.warnings["grossReceiptsId"]
                        ?.message
                    }
                    label={langStore.TenantProfileGrossReceiptsIdLabel}
                    onChange={(value: string) => onGrossReceiptsIdChange(value)}
                    nullText={langStore.optionalFieldHint}
                    description={
                      langStore.TenantProfileGrossReceiptsIdDescription
                    }
                  />
                  <InputString
                    value={billingEmail}
                    error={Boolean(billingEmailError)}
                    warning={
                      !billingEmailError &&
                      Boolean(tenantProfileUpdateStore.warnings["billingEmail"])
                    }
                    helperText={
                      billingEmailError ||
                      tenantProfileUpdateStore.warnings["billingEmail"]?.message
                    }
                    label={langStore.TenantProfileBillingEmailLabel}
                    onChange={(value: string) => onBillingEmailChange(value)}
                    nullText={langStore.optionalFieldHint}
                    description={langStore.TenantProfileBillingEmailDescription}
                  />
                  <InputReference
                    id="billingCurrency"
                    label={langStore.TenantProfileBillingCurrencyLabel}
                    error={Boolean(billingCurrencyError)}
                    helperText={
                      billingCurrencyError ||
                      tenantProfileUpdateStore.warnings["billingCurrency"]
                        ?.message
                    }
                    selectedValue={billingCurrencySelectedValue}
                    onChange={(value: string) => onBillingCurrencyChange(value)}
                    onSelect={(
                      value: { displayId: string; id: string } | null
                    ) => onBillingCurrencySelected(value)}
                    options={billingCurrencyOptions}
                    description={
                      langStore.TenantProfileBillingCurrencyDescription
                    }
                    nullText={langStore.optionalFieldHint}
                    canCreate={rootStore.authStore.hasPermissions(
                      "currency_create"
                    )}
                    renderCreateDialog={(dialogProps) => (
                      <CreateEntityDialog
                        {...dialogProps}
                        createStore={(rootStore) =>
                          new CurrencyCreateStore(rootStore)
                        }
                        renderForm={(store) => (
                          <CurrencyCreate
                            currencyCreateStore={store}
                            showToolbox={false}
                            showSaveFooter={false}
                          />
                        )}
                        saveMethod="saveNewCurrency"
                        cancelMethod="cancelNewCurrency"
                      />
                    )}
                  />
                </FormControl>
              </GroupContainer>

              <GroupContainer
                id="printer"
                errorText={tenantProfileUpdateStore.getCardErrors("printer")}
                title={langStore.groupTenantProfilePrinterLabel}
                collapsible={true}
                expanded={tenantProfileUpdateStore.getCardState("printer")}
                toggleExpand={() =>
                  tenantProfileUpdateStore.toggleCardState("printer")
                }
              >
                <FormControl fullWidth>
                  <InputString
                    value={printerName}
                    error={Boolean(printerNameError)}
                    warning={
                      !printerNameError &&
                      Boolean(tenantProfileUpdateStore.warnings["printerName"])
                    }
                    helperText={
                      printerNameError ||
                      tenantProfileUpdateStore.warnings["printerName"]?.message
                    }
                    label={langStore.TenantProfilePrinterNameLabel}
                    onChange={(value: string) => onPrinterNameChange(value)}
                    nullText={langStore.optionalFieldHint}
                    description={langStore.TenantProfilePrinterNameDescription}
                  />
                  <InputString
                    value={printerStreetAndNumber}
                    error={Boolean(printerStreetAndNumberError)}
                    warning={
                      !printerStreetAndNumberError &&
                      Boolean(
                        tenantProfileUpdateStore.warnings[
                          "printerStreetAndNumber"
                        ]
                      )
                    }
                    helperText={
                      printerStreetAndNumberError ||
                      tenantProfileUpdateStore.warnings[
                        "printerStreetAndNumber"
                      ]?.message
                    }
                    label={langStore.TenantProfilePrinterStreetAndNumberLabel}
                    onChange={(value: string) =>
                      onPrinterStreetAndNumberChange(value)
                    }
                    nullText={langStore.optionalFieldHint}
                    description={
                      langStore.TenantProfilePrinterStreetAndNumberDescription
                    }
                  />
                  <InputString
                    value={printerFloor}
                    error={Boolean(printerFloorError)}
                    warning={
                      !printerFloorError &&
                      Boolean(tenantProfileUpdateStore.warnings["printerFloor"])
                    }
                    helperText={
                      printerFloorError ||
                      tenantProfileUpdateStore.warnings["printerFloor"]?.message
                    }
                    label={langStore.TenantProfilePrinterFloorLabel}
                    onChange={(value: string) => onPrinterFloorChange(value)}
                    nullText={langStore.optionalFieldHint}
                    description={langStore.TenantProfilePrinterFloorDescription}
                  />
                  <InputString
                    value={printerSuite}
                    error={Boolean(printerSuiteError)}
                    warning={
                      !printerSuiteError &&
                      Boolean(tenantProfileUpdateStore.warnings["printerSuite"])
                    }
                    helperText={
                      printerSuiteError ||
                      tenantProfileUpdateStore.warnings["printerSuite"]?.message
                    }
                    label={langStore.TenantProfilePrinterSuiteLabel}
                    onChange={(value: string) => onPrinterSuiteChange(value)}
                    nullText={langStore.optionalFieldHint}
                    description={langStore.TenantProfilePrinterSuiteDescription}
                  />
                  <InputString
                    value={printerPostalCode}
                    error={Boolean(printerPostalCodeError)}
                    warning={
                      !printerPostalCodeError &&
                      Boolean(
                        tenantProfileUpdateStore.warnings["printerPostalCode"]
                      )
                    }
                    helperText={
                      printerPostalCodeError ||
                      tenantProfileUpdateStore.warnings["printerPostalCode"]
                        ?.message
                    }
                    label={langStore.TenantProfilePrinterPostalCodeLabel}
                    onChange={(value: string) =>
                      onPrinterPostalCodeChange(value)
                    }
                    nullText={langStore.optionalFieldHint}
                    description={
                      langStore.TenantProfilePrinterPostalCodeDescription
                    }
                  />
                  <InputReference
                    id="printerCountry"
                    label={langStore.TenantProfilePrinterCountryLabel}
                    error={Boolean(printerCountryError)}
                    helperText={
                      printerCountryError ||
                      tenantProfileUpdateStore.warnings["printerCountry"]
                        ?.message
                    }
                    selectedValue={printerCountrySelectedValue}
                    onChange={(value: string) => onPrinterCountryChange(value)}
                    onSelect={(
                      value: { displayId: string; id: string } | null
                    ) => onPrinterCountrySelected(value)}
                    options={printerCountryOptions}
                    description={
                      langStore.TenantProfilePrinterCountryDescription
                    }
                    nullText={langStore.optionalFieldHint}
                    canCreate={rootStore.authStore.hasPermissions(
                      "country_create"
                    )}
                    renderCreateDialog={(dialogProps) => (
                      <CreateEntityDialog
                        {...dialogProps}
                        createStore={(rootStore) =>
                          new CountryCreateStore(rootStore)
                        }
                        renderForm={(store) => (
                          <CountryCreate
                            countryCreateStore={store}
                            showToolbox={false}
                            showSaveFooter={false}
                          />
                        )}
                        saveMethod="saveNewCountry"
                        cancelMethod="cancelNewCountry"
                      />
                    )}
                  />
                  <InputReference
                    id="printerProvince"
                    label={langStore.TenantProfilePrinterProvinceLabel}
                    error={Boolean(printerProvinceError)}
                    helperText={
                      printerProvinceError ||
                      tenantProfileUpdateStore.warnings["printerProvince"]
                        ?.message
                    }
                    selectedValue={printerProvinceSelectedValue}
                    onChange={(value: string) => onPrinterProvinceChange(value)}
                    onSelect={(
                      value: { displayId: string; id: string } | null
                    ) => onPrinterProvinceSelected(value)}
                    options={printerProvinceOptions}
                    description={
                      langStore.TenantProfilePrinterProvinceDescription
                    }
                    nullText={langStore.optionalFieldHint}
                    canCreate={rootStore.authStore.hasPermissions(
                      "provinceState_create"
                    )}
                    renderCreateDialog={(dialogProps) => (
                      <CreateEntityDialog
                        {...dialogProps}
                        createStore={(rootStore) =>
                          new ProvinceStateCreateStore(rootStore)
                        }
                        renderForm={(store) => (
                          <ProvinceStateCreate
                            provinceStateCreateStore={store}
                            showToolbox={false}
                            showSaveFooter={false}
                          />
                        )}
                        saveMethod="saveNewProvinceState"
                        cancelMethod="cancelNewProvinceState"
                      />
                    )}
                  />
                  <InputString
                    value={printerCity}
                    error={Boolean(printerCityError)}
                    warning={
                      !printerCityError &&
                      Boolean(tenantProfileUpdateStore.warnings["printerCity"])
                    }
                    helperText={
                      printerCityError ||
                      tenantProfileUpdateStore.warnings["printerCity"]?.message
                    }
                    label={langStore.TenantProfilePrinterCityLabel}
                    onChange={(value: string) => onPrinterCityChange(value)}
                    nullText={langStore.optionalFieldHint}
                    description={langStore.TenantProfilePrinterCityDescription}
                  />
                </FormControl>
              </GroupContainer>

              <GroupContainer
                id="integrationsSadaic"
                errorText={tenantProfileUpdateStore.getCardErrors(
                  "integrationsSadaic"
                )}
                title={langStore.groupTenantProfileIntegrationsSadaicLabel}
                description={
                  langStore.groupTenantProfileIntegrationsSadaicDescription
                }
                collapsible={true}
                expanded={tenantProfileUpdateStore.getCardState(
                  "integrationsSadaic"
                )}
                toggleExpand={() =>
                  tenantProfileUpdateStore.toggleCardState("integrationsSadaic")
                }
              >
                <FormControl fullWidth>
                  <InputString
                    value={sadaicFtpUrl}
                    error={Boolean(sadaicFtpUrlError)}
                    warning={
                      !sadaicFtpUrlError &&
                      Boolean(tenantProfileUpdateStore.warnings["sadaicFtpUrl"])
                    }
                    helperText={
                      sadaicFtpUrlError ||
                      tenantProfileUpdateStore.warnings["sadaicFtpUrl"]?.message
                    }
                    label={langStore.TenantProfileSadaicFtpUrlLabel}
                    onChange={(value: string) => onSadaicFtpUrlChange(value)}
                    nullText={langStore.optionalFieldHint}
                    description={langStore.TenantProfileSadaicFtpUrlDescription}
                  />
                  <InputString
                    value={sadaicFtpUser}
                    error={Boolean(sadaicFtpUserError)}
                    warning={
                      !sadaicFtpUserError &&
                      Boolean(
                        tenantProfileUpdateStore.warnings["sadaicFtpUser"]
                      )
                    }
                    helperText={
                      sadaicFtpUserError ||
                      tenantProfileUpdateStore.warnings["sadaicFtpUser"]
                        ?.message
                    }
                    label={langStore.TenantProfileSadaicFtpUserLabel}
                    onChange={(value: string) => onSadaicFtpUserChange(value)}
                    nullText={langStore.optionalFieldHint}
                    description={
                      langStore.TenantProfileSadaicFtpUserDescription
                    }
                  />
                  <InputSecret
                    value={sadaicFtpPassword}
                    error={Boolean(sadaicFtpPasswordError)}
                    helperText={
                      sadaicFtpPasswordError ||
                      tenantProfileUpdateStore.warnings["sadaicFtpPassword"]
                        ?.message
                    }
                    label={langStore.TenantProfileSadaicFtpPasswordLabel}
                    onChange={(value: string) =>
                      onSadaicFtpPasswordChange(value)
                    }
                    description={
                      langStore.TenantProfileSadaicFtpPasswordDescription
                    }
                  />
                  <InputString
                    value={sadaicEmail}
                    error={Boolean(sadaicEmailError)}
                    warning={
                      !sadaicEmailError &&
                      Boolean(tenantProfileUpdateStore.warnings["sadaicEmail"])
                    }
                    helperText={
                      sadaicEmailError ||
                      tenantProfileUpdateStore.warnings["sadaicEmail"]?.message
                    }
                    label={langStore.TenantProfileSadaicEmailLabel}
                    onChange={(value: string) => onSadaicEmailChange(value)}
                    nullText={langStore.optionalFieldHint}
                    description={langStore.TenantProfileSadaicEmailDescription}
                  />
                </FormControl>
              </GroupContainer>
            </FormRecordProvider>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
